import type { FeatureFlags } from '../../../../types/FeatureFlags'
import type {
  AccountApiAccess,
  ApiToken,
  ApiTokenFormValues,
  ApiTokenPayload,
} from '../types'

/**
 * Reads the account's `feature_flags` (from `GET /account/:id`) into what the
 * account settings show: the API Access tab, the Website Plugin tab, and which
 * token abilities can be chosen.
 */
export const getAccountApiAccess = (
  featureFlags: Partial<FeatureFlags> | null | undefined
): AccountApiAccess => {
  const canRead = featureFlags?.['api-read'] === true
  const canWrite = featureFlags?.['api-write'] === true

  return {
    canRead,
    canWrite,
    hasApiAccess: canRead || canWrite,
    hasWebsitePlugin: featureFlags?.['website-plugin'] === true,
  }
}

/** The create form's starting switches: on for whatever the account allows. */
export const apiTokenInitialValues = (
  access: AccountApiAccess
): ApiTokenFormValues => ({
  name: '',
  canRead: access.canRead,
  canWrite: access.canWrite,
})

export const toApiTokenPayload = (
  values: ApiTokenFormValues
): ApiTokenPayload => ({
  name: values.name.trim(),
  abilities: [
    ...(values.canRead ? (['read'] as const) : []),
    ...(values.canWrite ? (['write'] as const) : []),
  ],
})

/** The token list's Access column. */
export const formatApiTokenAccess = (abilities: ApiToken['abilities']) => {
  if (abilities.includes('*')) {
    return 'Full'
  }
  const canRead = abilities.includes('read')
  const canWrite = abilities.includes('write')
  if (canRead && canWrite) {
    return 'Read & Write'
  }
  if (canWrite) {
    return 'Write'
  }
  return canRead ? 'Read' : 'None'
}
